import { KaboodleToaster } from "components/common";
import Documents from "components/Documents/drawer/DocumentsDrawer";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import LoadingIcon from "components/Loading/LoadingIcon";
import CreateReReviewModal from "components/Modals/CreateReReviewModal";
import TaskModal from "pages/tasks/components/modals/TaskModal";

import CodeDirectoryDrawer from "components/CodeDirectory/CodeDirectoryDrawer";

import {
  isCmsSubmissionSection,
  SECTION,
  serviceTypeHasCmsSubmission,
} from "constants/SettingsConstants";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import AdjusterInformationForm from "pages/files/sections/AdjusterInformation";
import DefenseAttorneyInformationForm from "pages/files/sections/DefenseAttorneyInformation";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { clearCurrentTask } from "reducers/files";
import {
  setCurrentSection,
  setIsLoading,
  setShowCreateReReviewModal,
  setShowTaskModal,
} from "reducers/main";
import { usePatchClaimantByIdMutation } from "services/claimant-api";
import {
  usePatchDateOfInjuryByIdMutation,
  usePatchServiceDateOfInjuryByIdMutation,
} from "services/doi-api.ts";
import { usePatchFileByIdMutation } from "services/file-api.ts";
import { useGetMSAByIdQuery, usePatchMSAByIdMutation } from "services/msa-api";
import {
  useGetPartABPlanCoveragesQuery,
  useGetPartCDPlanCoveragesQuery,
} from "services/part-coverage-api";
import { useToken } from "utils/context";
import { useDispatch, useSelector } from "utils/redux";

import MSAOverview from "../FMOverview";
import { setCoverageFormValues } from "../shared/coverage-utils";
import { FutureMedicalBackgroundJobs } from "../shared/FutureMedicalBackgroundJobs";
import FutureMedicalClaimantInformation from "../shared/FMClaimantInformation";
import FutureMedicalClinicianNotes from "../shared/FMClinicianNotes";
import FutureMedicalLegalNotes from "../shared/FMLegalNotes";
import FutureMedicalRightPanel from "../shared/FMRightPanel";
import FutureMedicalMedicalHistoryReview from "../shared/medical-history/FMMedicalHistoryReview";
import FMRxHistory from "../shared/medical-history/FMRxHistory";
import MSAPageContainer from "./MSAPageContainer";

const MSASectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { reset, setError, clearErrors, setValue } = useFormContext();
  const dispatch = useDispatch();
  const { msaSection } = useParams<{ msaSection: string }>();
  const [patchFileById] = usePatchFileByIdMutation();
  const { accessToken } = useToken();
  const { msaId } = useParams<{ msaId?: string }>();
  const [patchMsaById] = usePatchMSAByIdMutation();
  const showTaskModal = useSelector((state) => state.main.showTaskModal);
  const showAddReReviewModal = useSelector(
    (state) => state.main.showCreateReReviewModal,
  );
  const isAmendedReview = location.pathname.includes("/amended-review/");
  const _serviceType = isAmendedReview ? "ar" : "msa";

  useEffect(() => {
    dispatch(
      setCurrentSection(
        _serviceType === "msa" ? SECTION.MSA : SECTION.AMENDED_REVIEW,
      ),
    );
  }, [dispatch, _serviceType]);

  const {
    data: msa,
    isLoading: msaLoading,
    isError,
  } = useGetMSAByIdQuery({ msaId: msaId }, { skip: !msaId });

  const searchParams = new URLSearchParams(location.search);
  const fileId = searchParams.get("file_id");

  // use the MSA service object directly with DOI hook
  const { selectedServiceDoi } = useServiceDOI(msa);

  // fetch plan coverages by file_id
  const { data: partABCoverages = [] } = useGetPartABPlanCoveragesQuery(
    { file_id: msa?.file?.id },
    { skip: !msa?.file?.id },
  );

  const { data: partCDCoverages = [] } = useGetPartCDPlanCoveragesQuery(
    { file_id: msa?.file?.id },
    { skip: !msa?.file?.id },
  );

  const [patchClaimantById] = usePatchClaimantByIdMutation();
  const [patchServiceDateOfInjuryById] =
    usePatchServiceDateOfInjuryByIdMutation();
  const [patchDateOfInjuryById] = usePatchDateOfInjuryByIdMutation();

  const getField = (field: string) =>
    field?.includes(".") ? field.split(".")[1] : field;

  const handleSaveMsa = useCallback(
    async (field: string | null, value: unknown) => {
      if (msaId && accessToken) {
        try {
          if (field?.startsWith?.("claimant.")) {
            // handle claimant fields
            await patchClaimantById({
              id: msa.claimant.id.toString(),
              data: {
                [getField(field)]: value,
              },
            }).unwrap();
          } else if (field?.startsWith?.("service_date_of_injury.")) {
            // handle service date of injury fields
            if (selectedServiceDoi?.id) {
              await patchServiceDateOfInjuryById({
                id: selectedServiceDoi.id.toString(),
                data: {
                  [getField(field)]: value,
                },
              }).unwrap();
            } else {
              console.error(
                "Cannot update DOI: selectedDoi is null or id is missing",
              );
            }
          } else if (field?.startsWith?.("date_of_injury.")) {
            // handle date of injury fields
            if (selectedServiceDoi?.id && selectedServiceDoi?.date_of_injury) {
              // The actual DateOfInjury ID is in the date_of_injury field of the ServiceDateOfInjury object
              await patchDateOfInjuryById({
                id: selectedServiceDoi.date_of_injury.toString(),
                data: {
                  [getField(field)]: value,
                },
              }).unwrap();
            } else {
              KaboodleToaster.error(
                "Cannot update DOI: selectedDoi is null or date_of_injury is missing",
              );
              console.error(
                "Cannot update DOI: selectedDoi is null or date_of_injury is missing",
              );
            }
          } else if (field?.startsWith?.("file.")) {
            // handle file fields
            await patchFileById({
              id: fileId?.toString() || msa?.file?.id.toString(),
              data: {
                [getField(field)]: value,
              },
            });
          } else {
            // if it's a bulk update (null/empty field), pass value directly
            const data = !field ? value : { [field]: value };
            await patchMsaById({
              msaId: msaId,
              data,
            }).unwrap();
          }
          clearErrors(field || "");
        } catch (error) {
          console.error("Error saving MSA:", error);
          setError(field || "", {
            type: error.status,
            message: error.data[field]?.[0] || "An error occurred",
          });
        }
      }
    },
    [
      msa,

      patchMsaById,
      patchClaimantById,
      patchDateOfInjuryById,
      msaId,
      clearErrors,
      setError,
      fileId,
      patchFileById,
      selectedServiceDoi,
    ],
  );

  const handleCloseTaskModal = useCallback(() => {
    dispatch(clearCurrentTask());
    dispatch(setShowTaskModal(false));
  }, [dispatch]);

  const handleCloseReReviewModal = useCallback(() => {
    dispatch(setShowCreateReReviewModal(false));
  }, [dispatch]);

  const addTaskModal = useMemo(
    () => (
      <TaskModal
        isOpen={showTaskModal}
        onClose={handleCloseTaskModal}
        fileId={msa?.file?.id.toString()}
        serviceId={msa?.service}
      />
    ),
    [showTaskModal, handleCloseTaskModal, msa],
  );

  const addReReviewModal = useMemo(
    () => (
      <CreateReReviewModal
        isOpen={showAddReReviewModal}
        msaId={msa?.id}
        onClose={handleCloseReReviewModal}
      />
    ),
    [showAddReReviewModal, handleCloseReReviewModal, msa],
  );

  // redirect to 404 if msa is not found
  useEffect(() => {
    if (!msaLoading && (!msa || isError)) {
      navigate("/404");
    }
  }, [msa, msaLoading, isError, history]);

  // reset the form if we have msa data
  useEffect(() => {
    if (!msaLoading && msa) {
      reset(msa, { keepDirtyValues: true });
      // transform coverage data into form values and set them
      // ensure we have arrays even if data is undefined
      const planCoverages = {
        part_a_b_coverages: Array.isArray(partABCoverages)
          ? partABCoverages
          : [],
        part_c_d_coverages: Array.isArray(partCDCoverages)
          ? partCDCoverages
          : [],
      };
      setCoverageFormValues(planCoverages, setValue);
    }
  }, [msa, reset, msaLoading, setValue, partABCoverages, partCDCoverages]);

  useEffect(() => {
    dispatch(setIsLoading(msaLoading));
  }, [dispatch, msaLoading]);

  const isOverviewPage = location.pathname.endsWith("/overview");

  if (msaLoading) {
    return <LoadingIcon />;
  }

  if (!msa) {
    return null;
  }

  // the CMS Submission / Invoice (CMS) / CMS Status / F.S.D routes don't exist
  // for NTMSA, MSA-111 and Pre-MSA Snapshot; send stale links to Operations instead
  if (
    isCmsSubmissionSection(msaSection) &&
    !serviceTypeHasCmsSubmission(msa.type)
  ) {
    return <Navigate to={`/future-medical/msa/${msa.id}/operations`} replace />;
  }

  // on legal and medical review pages, we don't need the claimant information section, so make layout full
  const isReviewPage =
    msaSection === "legal-review" || msaSection === "medical-review";

  const isClientDeliveryPage = msaSection === "client-delivery";

  return (
    <MSAPageContainer
      layout={isOverviewPage || isReviewPage ? "full" : "split"}
      msa={msa}
    >
      {addTaskModal}
      {isOverviewPage ? (
        <MSAOverview />
      ) : (
        <>
          {!isReviewPage && !isClientDeliveryPage && (
            <FutureMedicalClaimantInformation
              onSave={handleSaveMsa}
              service={msa}
              section={msaSection}
            />
          )}
          {isClientDeliveryPage && (
            <>
              <div
                className={`FutureMedical_Adjuster_DefenseAttorneyInformation`}
              >
                <div className="swim-headers">
                  Adjuster and Defense Attorney Information
                </div>
                <div className="swim-column">
                  <AdjusterInformationForm
                    fileFromServiceId={msa.file.id.toString()}
                  />
                  <DefenseAttorneyInformationForm
                    fileId={msa.file.id.toString()}
                  />
                </div>
              </div>
            </>
          )}
          <FutureMedicalRightPanel
            section={msaSection}
            service={msa}
            onSave={handleSaveMsa}
          />
        </>
      )}
      <FutureMedicalLegalNotes service={msa} onSave={handleSaveMsa} />
      <FutureMedicalClinicianNotes service={msa} onSave={handleSaveMsa} />
      <ClientInformationDrawer service={msa} />
      <FutureMedicalBackgroundJobs fileId={msa?.file?.id} />
      <FutureMedicalMedicalHistoryReview service={msa} />

      <FMRxHistory service={msa} />
      <Documents fileId={msa?.file?.id} serviceId={msa?.id} serviceType="msa" />
      <CodeDirectoryDrawer />
      {addReReviewModal}
    </MSAPageContainer>
  );
};

export default MSASectionPage;
